import type { LitSquadCraftPreviewItem } from '@/lib/api';

const RARITY_STYLES: Record<string, string> = {
  common: 'bg-zinc-500/15 text-zinc-700 dark:text-zinc-200',
  uncommon: 'bg-green-500/15 text-green-700 dark:text-green-300',
  rare: 'bg-blue-500/15 text-blue-700 dark:text-blue-300',
  epic: 'bg-purple-500/15 text-purple-700 dark:text-purple-300',
  legendary: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
};

interface CraftOutcomeCardProps {
  outcome: LitSquadCraftPreviewItem;
}

/**
 * One tile in the preview grid: thumbnail, display name, rarity badge,
 * and computed probability percentage.
 */
export function CraftOutcomeCard({ outcome }: CraftOutcomeCardProps) {
  const percent = outcome.probability * 100;
  const percentLabel = percent >= 1 ? `${percent.toFixed(1)}%` : `${percent.toFixed(2)}%`;
  const rarityClass = RARITY_STYLES[outcome.rarity] ?? RARITY_STYLES.common;

  return (
    <div className="rounded-lg border border-border bg-card p-3 flex flex-col gap-2">
      <div className="aspect-square relative overflow-hidden rounded-md bg-muted">
        <img
          src={outcome.thumbnailUri}
          alt={outcome.displayName}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={(e) => {
            // Graceful fallback when the legacy S3 thumbnail 404s.
            (e.target as HTMLImageElement).style.visibility = 'hidden';
          }}
        />
      </div>
      <div className="min-h-[2.5rem]">
        <div className="text-sm font-medium line-clamp-2">{outcome.partialName}</div>
      </div>
      <div className="flex items-center justify-between gap-2">
        <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${rarityClass}`}>
          {outcome.rarity}
        </span>
        <span className="text-xs font-mono tabular-nums">{percentLabel}</span>
      </div>
    </div>
  );
}
